import { useState, useMemo, useCallback } from 'react'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import type { View } from 'react-big-calendar'
import moment from 'moment'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import '@/styles/calendar.css'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiService } from '@/services/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Calendar as CalendarIcon, RefreshCw, Plus, Loader2, Save, Send } from 'lucide-react'
import { toast } from 'sonner'
import { useDrawer } from '@/components/ui/drawer'
import { BlogPostFormDrawer } from '@/components/blog/BlogPostFormDrawer'
import { format } from 'date-fns'

// Setup the localizer for react-big-calendar
const localizer = momentLocalizer(moment)

// Enable drag and drop
const DnDCalendar = withDragAndDrop(Calendar)

interface BlogPost {
  id: string
  slug: string
  title: string
  content: string
  excerpt?: string
  status: 'draft' | 'review' | 'published' | 'scheduled'
  view_count: number
  created_at: string
  updated_at: string
  published_at?: string
  scheduled_at?: string
  reading_time_minutes?: number
  categories: Array<{ id: string; name: string; slug: string; color?: string }>
  tags: Array<{ id: string; name: string; slug: string }>
}

interface CalendarEvent {
  id: string
  title: string
  start: Date
  end: Date
  resource: BlogPost
}

export default function BlogCalendar() {
  const [view, setView] = useState<View>('month')
  const [date, setDate] = useState(new Date())
  const { openDrawer, closeDrawer, updateDrawer } = useDrawer()
  const queryClient = useQueryClient()

  // Fetch blog posts
  const {
    data: posts,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['blog-posts-calendar'],
    queryFn: async () => {
      const response = await apiService.blog.posts.list()
      return response || []
    },
  })

  // Mutation for rescheduling posts
  const rescheduleMutation = useMutation({
    mutationFn: async ({
      postId,
      newDate,
    }: {
      postId: string
      newDate: Date
    }) => {
      return await apiService.blog.posts.schedule(postId, {
        scheduled_at: newDate.toISOString(),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-posts-calendar'] })
      toast.success('Post rescheduled successfully')
    },
    onError: (error: any) => {
      toast.error(`Failed to reschedule post: ${error.message}`)
    },
  })

  // Transform blog posts to calendar events
  const events: CalendarEvent[] = useMemo(() => {
    if (!posts) return []

    return posts
      .filter((post: BlogPost) => post.scheduled_at || post.published_at)
      .map((post: BlogPost) => {
        const eventDate = post.scheduled_at
          ? new Date(post.scheduled_at)
          : new Date(post.published_at!)

        return {
          id: post.id,
          title: post.title,
          start: eventDate,
          end: new Date(eventDate.getTime() + 60 * 60 * 1000), // 1 hour duration
          resource: post,
        }
      })
  }, [posts])

  // Helper function to create footer for blog post drawers
  const createBlogPostFooter = useCallback((
    drawerId: string,
    state: {
      loading: boolean
      isSaving: boolean
      lastSaved: Date | null
      hasUnsavedChanges: boolean
      handleSubmit: (action: 'draft' | 'review' | 'publish' | 'schedule') => Promise<void>
      status: 'draft' | 'review' | 'published' | 'scheduled'
    },
    post?: BlogPost
  ) => {
    return (
      <div className="p-4 flex items-center justify-between">
        <div className="text-sm flex items-center gap-2">
          {state.isSaving && (
            <span className="flex items-center gap-2 text-blue-600 font-medium">
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </span>
          )}
          {!state.isSaving && state.lastSaved && (
            <span className="flex items-center gap-2 text-blue-600">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              All changes saved at {format(state.lastSaved, 'HH:mm:ss')}
            </span>
          )}
          {!state.isSaving && !state.lastSaved && state.hasUnsavedChanges && (
            <span className="text-amber-600 font-medium">Unsaved changes</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => closeDrawer(drawerId)}
            disabled={state.loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => state.handleSubmit('draft')}
            disabled={state.loading}
          >
            {state.loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="mr-2 h-4 w-4" />
            Save as Draft
          </Button>
          {state.status !== 'published' && (
            <Button
              type="button"
              variant="outline"
              onClick={() => state.handleSubmit('review')}
              disabled={state.loading}
            >
              {state.loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Send className="mr-2 h-4 w-4" />
              Submit for Review
            </Button>
          )}
          <Button
            type="button"
            onClick={() => state.handleSubmit('publish')}
            disabled={state.loading}
          >
            {state.loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {post ? 'Update Post' : 'Publish Now'}
          </Button>
        </div>
      </div>
    )
  }, [closeDrawer])

  // Handle event drop (drag and drop)
  const handleEventDrop = useCallback(
    ({ event, start }: { event: CalendarEvent; start: Date }) => {
      const post = event.resource

      // Only allow rescheduling for scheduled or draft posts
      if (post.status === 'published') {
        toast.error('Cannot reschedule published posts')
        return
      }

      rescheduleMutation.mutate({ postId: post.id, newDate: start })
    },
    [rescheduleMutation]
  )

  // Handle event resize (not implemented for simplicity)
  const handleEventResize = useCallback(
    () => {
      // Could implement duration change here if needed
      toast.info('Event resize not implemented')
    },
    []
  )

  // Handle selecting an event (click on event)
  const handleSelectEvent = useCallback(
    (event: CalendarEvent) => {
      const post = event.resource
      const drawerId = `edit-blog-post-${post.id}`
      openDrawer({
        id: drawerId,
        title: 'Edit Article',
        content: <BlogPostFormDrawer
          post={post}
          onClose={() => closeDrawer(drawerId)}
          onFormStateChange={(state) => {
            updateDrawer(drawerId, {
              footer: createBlogPostFooter(drawerId, state, post)
            })
          }}
        />,
        size: '80',
        footer: <div className="p-4" />, // Initial empty footer
      })
    },
    [openDrawer, closeDrawer, updateDrawer, createBlogPostFooter]
  )

  // Handle selecting a slot (click on empty calendar slot)
  const handleSelectSlot = useCallback(
    ({ start }: { start: Date; end: Date }) => {
      // Create a draft post object with the selected date as scheduled_at
      const draftPost = {
        id: '',
        slug: '',
        title: '',
        content: '',
        status: 'scheduled' as const,
        scheduled_at: start.toISOString(),
        categories: [],
        tags: [],
      }

      const drawerId = `create-scheduled-post-${start.getTime()}`
      openDrawer({
        id: drawerId,
        title: 'Create Scheduled Post',
        content: <BlogPostFormDrawer
          post={draftPost as any}
          onClose={() => closeDrawer(drawerId)}
          onFormStateChange={(state) => {
            updateDrawer(drawerId, {
              footer: createBlogPostFooter(drawerId, state)
            })
          }}
        />,
        size: '80',
        footer: <div className="p-4" />, // Initial empty footer
      })
    },
    [openDrawer, closeDrawer, updateDrawer, createBlogPostFooter]
  )

  // Custom event style getter for dark mode support
  const eventStyleGetter = useCallback(
    (event: CalendarEvent) => {
      const post = event.resource
      let backgroundColor = 'hsl(var(--primary))'
      let borderColor = 'hsl(var(--primary))'

      // Color code by status
      switch (post.status) {
        case 'draft':
          backgroundColor = 'hsl(var(--muted))'
          borderColor = 'hsl(var(--muted-foreground))'
          break
        case 'review':
          backgroundColor = 'hsl(var(--warning))'
          borderColor = 'hsl(var(--warning))'
          break
        case 'scheduled':
          backgroundColor = 'hsl(var(--primary))'
          borderColor = 'hsl(var(--primary))'
          break
        case 'published':
          backgroundColor = 'hsl(var(--primary))'
          borderColor = 'hsl(var(--primary))'
          break
      }

      return {
        style: {
          backgroundColor,
          borderColor,
          color: 'hsl(var(--primary-foreground))',
          border: `1px solid ${borderColor}`,
          borderRadius: '4px',
        },
      }
    },
    []
  )

  // Custom day prop getter for dark mode support
  const dayPropGetter = useCallback(() => {
    return {
      style: {
        backgroundColor: 'transparent',
      },
    }
  }, [])

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <Card className="bg-card text-card-foreground border-border">
          <CardContent className="pt-6">
            <p className="text-destructive">Error loading calendar: {error.message}</p>
            <Button onClick={() => refetch()} className="mt-4">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CalendarIcon className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Editorial Calendar
              </h1>
              <p className="text-sm text-muted-foreground">
                Schedule and manage blog post publishing dates
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
              className="border-border hover:bg-accent"
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
              />
              <span className="ml-2">Refresh</span>
            </Button>

            <Button
              size="sm"
              onClick={() => handleSelectSlot({ start: new Date(), end: new Date() })}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              <span className="ml-2">New Post</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="border-b border-border bg-muted/50 px-6 py-3">
        <div className="flex gap-6 text-sm">
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="bg-primary/10 text-primary border-primary"
            >
              Scheduled
            </Badge>
            <span className="text-muted-foreground">
              {posts?.filter((p: BlogPost) => p.status === 'scheduled').length || 0}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500"
            >
              Published
            </Badge>
            <span className="text-muted-foreground">
              {posts?.filter((p: BlogPost) => p.status === 'published').length || 0}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="bg-warning/10 text-warning border-warning"
            >
              In Review
            </Badge>
            <span className="text-muted-foreground">
              {posts?.filter((p: BlogPost) => p.status === 'review').length || 0}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="bg-muted text-muted-foreground border-muted-foreground"
            >
              Draft
            </Badge>
            <span className="text-muted-foreground">
              {posts?.filter((p: BlogPost) => p.status === 'draft').length || 0}
            </span>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="flex-1 overflow-hidden p-6">
        <Card className="h-full bg-card text-card-foreground border-border">
          <CardContent className="h-full p-6">
            <div className="calendar-dark-mode" style={{ height: '600px', minHeight: '600px' }}>
              <DnDCalendar
                localizer={localizer}
                events={events as any}
                view={view}
                date={date}
                onView={setView}
                onNavigate={setDate}
                onSelectEvent={handleSelectEvent as any}
                onSelectSlot={handleSelectSlot as any}
                onEventDrop={handleEventDrop as any}
                onEventResize={handleEventResize as any}
                eventPropGetter={eventStyleGetter as any}
                dayPropGetter={dayPropGetter as any}
                selectable
                resizable={false}
                draggableAccessor={() => true}
                style={{ height: '100%' }}
                views={['month', 'week', 'day', 'agenda']}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
